import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../Modal';
import PeoplePicker from '../PeoplePicker';
import Markdown from '../recording/Markdown';
import {
  announcementsApi, errorOf, AUDIENCE_LABEL, toLocalInput, fromLocalInput
} from '../../lib/announcements';
import './Announcements.css';

/**
 * Write or change an announcement (125), in a dialog. Administrators.
 *
 *   Title and text - plain text with simple formatting (# a heading,
 *   - a list, **bold**, *italics*, and links to Jarvis's own pages only,
 *   [words](/help/…) - Gordon, 3 November), with a preview drawn exactly as
 *   the banner and the feed draw it (components/recording/Markdown.js).
 *   Info or Important. Who it is for: everyone, staff only, contractors
 *   only, one or more departments, or named people (staff and/or
 *   contractors). When it starts - now, or a day and time ahead - and,
 *   optionally, ends. Also email it (off by default; only to people who
 *   want announcement emails, and only when this server can send mail -
 *   when it cannot, the box is off and says why).
 *
 * EDITING ONE ALREADY SHOWING makes a new version. When it is Important the
 * form asks, before it can be saved, whether everybody must acknowledge it
 * again - the server asks the same question (409 needs_choice) if the
 * answer is missing. Its start cannot move and it cannot be emailed again.
 */
export default function AnnouncementForm({ existing = null, onClose, onSaved }) {
  const a = existing;
  const published = Boolean(a?.published_at);
  const [options, setOptions] = useState(null);
  const [title, setTitle] = useState(a?.title || '');
  const [body, setBody] = useState(a?.body || '');
  const [importance, setImportance] = useState(a?.importance || 'info');
  const [audience, setAudience] = useState(a?.audience || 'everyone');
  const [departments, setDepartments] = useState(a?.departments || []);
  const [staffIds, setStaffIds] = useState((a?.people || []).filter((p) => p.kind === 'user').map((p) => p.id));
  const [conIds, setConIds] = useState((a?.people || []).filter((p) => p.kind === 'contractor').map((p) => p.id));
  const [startNow, setStartNow] = useState(!a || published || new Date(a.starts_at) <= new Date());
  const [startsAt, setStartsAt] = useState(a ? toLocalInput(a.starts_at) : '');
  const [endsAt, setEndsAt] = useState(a?.ends_at ? toLocalInput(a.ends_at) : '');
  const [email, setEmail] = useState(Boolean(a?.email));
  const [again, setAgain] = useState(null);
  const [preview, setPreview] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');

  useEffect(() => {
    announcementsApi.options().then(setOptions)
      .catch(() => setOptions({ staff: [], contractors: [], departments: [], mail: { configured: true } }));
  }, []);

  const staffOptions = useMemo(() => (options?.staff || []).map((p) => ({ id: p.id, name: p.name, company: p.department || '' })), [options]);
  const conOptions = useMemo(() => (options?.contractors || []).map((c) => ({ id: c.id, name: c.name, company: c.company || '' })), [options]);
  const mailOff = options && options.mail && options.mail.configured === false;
  // An edit to an Important one already showing has to say whether everybody reads it again.
  const mustAsk = published && a?.importance === 'important' && importance === 'important';

  const save = async () => {
    setProblem('');
    if (!title.trim()) { setProblem('Give it a title.'); return; }
    if (mustAsk && again === null) { setProblem('Say whether everybody must acknowledge it again.'); return; }
    const payload = {
      title: title.trim(), body, importance, audience,
      departments: audience === 'departments' ? departments : [],
      people: audience === 'people' ? [...staffIds.map((id) => ({ kind: 'user', id })), ...conIds.map((id) => ({ kind: 'contractor', id }))] : [],
      ends_at: fromLocalInput(endsAt)
    };
    if (!published) {
      payload.starts_at = startNow ? null : fromLocalInput(startsAt);
      payload.email = email && !mailOff;
    }
    if (mustAsk) payload.require_ack = again;
    setBusy(true);
    try {
      const r = a ? await announcementsApi.update(a.id, payload) : await announcementsApi.create(payload);
      onSaved(r);
    } catch (e) {
      if (e?.response?.data?.needs_choice) setProblem('Say whether everybody must acknowledge it again.');
      else setProblem(errorOf(e, 'Could not save the announcement.'));
    }
    setBusy(false);
  };

  const toggleDept = (d) => setDepartments((list) => (list.includes(d) ? list.filter((x) => x !== d) : [...list, d]));

  return (
    <Modal title={a ? 'Change the announcement' : 'Write an announcement'} wide onClose={onClose}
      subtitle={published ? `It is showing now: saving makes version ${(a.version || 1) + 1}.` : null}
      footer={(
        <div className="ann-actions">
          <button type="button" className="btn btn-plain" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={busy} onClick={save} data-testid="ann-save">
            {busy ? 'Saving…' : a ? 'Save' : startNow ? 'Publish' : 'Schedule'}
          </button>
        </div>
      )}>
      <div className="ann-form" data-testid="announcement-form">
        <div className="ann-field">
          <label htmlFor="ann-title">Title</label>
          <input id="ann-title" type="text" maxLength={160} value={title} onChange={(e) => setTitle(e.target.value)} />
        </div>
        <div className="ann-field">
          <label htmlFor="ann-body">Text</label>
          {preview
            ? <div className="ann-preview" data-testid="ann-preview">{body.trim() ? <Markdown text={body} className="ann-md" links /> : <p className="ann-quiet">Nothing written yet.</p>}</div>
            : <textarea id="ann-body" value={body} maxLength={20000} onChange={(e) => setBody(e.target.value)} />}
          <p className="ann-hint">
            Plain text. <code># </code>a heading, <code>- </code>a list, <code>**bold**</code>, <code>*italics*</code>,{' '}
            <code>[words](/help/…)</code> a button to a page in Jarvis (starting with /, such as /help/…, /request/… or /tickets/…).
            Addresses of other websites and email addresses stay plain text.{' '}
            <button type="button" className="ann-link" onClick={() => setPreview((p) => !p)}>{preview ? 'Back to writing' : 'Preview'}</button>
          </p>
        </div>

        <fieldset className="ann-field">
          <legend className="ann-legend">How important</legend>
          <div className="ann-choices">
            <label className="ann-choice"><input type="radio" name="ann-importance" checked={importance === 'info'} onChange={() => setImportance('info')} />Info - in the Announcements tab under the bell</label>
            <label className="ann-choice"><input type="radio" name="ann-importance" checked={importance === 'important'} onChange={() => setImportance('important')} />Important - a banner on every page until each person has read it</label>
          </div>
        </fieldset>

        <div className="ann-field">
          <label htmlFor="ann-audience">Who it is for</label>
          <select id="ann-audience" value={audience} onChange={(e) => setAudience(e.target.value)}>
            {Object.entries(AUDIENCE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        {audience === 'departments' && (
          <fieldset className="ann-field" data-testid="ann-departments">
            <legend className="ann-legend">Departments</legend>
            {(options?.departments || []).length === 0
              ? <p className="ann-hint">Nobody has a department yet (Settings → Users).</p>
              : (
                <div className="ann-choices">
                  {options.departments.map((d) => (
                    <label key={d} className="ann-choice"><input type="checkbox" checked={departments.includes(d)} onChange={() => toggleDept(d)} />{d}</label>
                  ))}
                </div>
              )}
          </fieldset>
        )}
        {audience === 'people' && (
          <div className="ann-row2">
            <div className="ann-field">
              <span className="ann-legend">Staff</span>
              <PeoplePicker label="Staff" options={staffOptions} value={staffIds} onChange={setStaffIds} allLabel="Nobody" idBase="ann-staff" />
            </div>
            <div className="ann-field">
              <span className="ann-legend">Contractors</span>
              <PeoplePicker label="Contractors" options={conOptions} value={conIds} onChange={setConIds} allLabel="Nobody" idBase="ann-cons" />
            </div>
          </div>
        )}

        <div className="ann-row2">
          {!published && (
            <fieldset className="ann-field">
              <legend className="ann-legend">Starts</legend>
              <div className="ann-choices">
                <label className="ann-choice"><input type="radio" name="ann-start" checked={startNow} onChange={() => setStartNow(true)} />Now</label>
                <label className="ann-choice"><input type="radio" name="ann-start" checked={!startNow} onChange={() => setStartNow(false)} />Later</label>
              </div>
              {!startNow && (
                <input type="datetime-local" aria-label="Starts at" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
              )}
            </fieldset>
          )}
          <div className="ann-field">
            <label htmlFor="ann-ends">Ends (optional)</label>
            <input id="ann-ends" type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
            <p className="ann-hint">Left empty, it shows until you end it.</p>
          </div>
        </div>

        {!published && (
          <div className="ann-field">
            <label className="ann-choice">
              <input type="checkbox" checked={email && !mailOff} disabled={mailOff} onChange={(e) => setEmail(e.target.checked)} data-testid="ann-email" />
              Also email it
            </label>
            <p className="ann-hint" data-testid="ann-email-hint">
              {mailOff
                ? 'Email is not set up on this server, so it cannot be emailed - it will still show in Jarvis.'
                : 'Sent when it starts, to everybody it is for who has not switched announcement emails off.'}
            </p>
          </div>
        )}

        {mustAsk && (
          <fieldset className="ann-field ann-ask" data-testid="ann-again">
            <legend className="ann-legend">Should everybody acknowledge it again?</legend>
            <div className="ann-choices">
              <label className="ann-choice"><input type="radio" name="ann-again" checked={again === true} onChange={() => setAgain(true)} />Yes - it has changed in a way that matters; every banner comes back</label>
              <label className="ann-choice"><input type="radio" name="ann-again" checked={again === false} onChange={() => setAgain(false)} />No - a small correction; acknowledgements already given still count</label>
            </div>
          </fieldset>
        )}

        {problem && <p className="ann-problem" role="alert">{problem}</p>}
      </div>
    </Modal>
  );
}
